import React, { createContext, ReactNode, useEffect, useContext, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';

import useFontsLoaded from 'hooks/useFontsLoaded';
import useDetectedLanguage from 'hooks/useDetectedLanguage';
import { getRequestParams, mapBackendDataToGames } from 'utils';
import { TGame, TBackendResponse } from 'types';

interface IGlobalContext {
  gameOfDay: TGame | null;
  lookALikeGames: TGame[];
  games: TGame[];
  loading: boolean;
  abTestId?: string;
}

interface IGlobalContextProvider {
  children?: ReactNode;
}

const GAMES_API_URL = 'https://catalog.saygames.io/api/v1/items';

const GlobalContext = createContext<IGlobalContext>({
  games: [],
  gameOfDay: null,
  lookALikeGames: [],
  loading: false,
});

export const GlobalContextProvider: React.FC<IGlobalContextProvider> = ({ children }) => {
  const { showBoundary } = useErrorBoundary();
  const areFontsLoaded = useFontsLoaded();
  const [games, setGames] = useState<TGame[]>([]);
  const [abTestId, setAbTestId] = useState<string>();
  const [loadingGames, setLoadingGames] = useState<boolean>(true);
  const [lookALikeGameIds, setLookALikeGameIds] = useState<string[]>([]);

  useDetectedLanguage(loadingGames);

  useEffect(() => {
    const loadGames = async () => {
      try {
        const requestParams = getRequestParams();
        const response = await fetch(GAMES_API_URL + `?${requestParams}`);
        const json = await response.json() as TBackendResponse;

        if (json.code || json.message) {
          throw new Error(json.message);
        }

        setGames(mapBackendDataToGames(json));
        setLookALikeGameIds(json.games_like);
        setAbTestId(json.ab_test_id);
        setLoadingGames(false);

        // WARN: Set request_id and ab_test_id to window object in order to reuse it outside from the WebView.
        (window as any).request_id = json.request_id;
        (window as any).ab_test_id = json.ab_test_id;

        // WARN: If the 'show' event was not tracked yet, then we should re-call it once request_id is set.
        if ((window as any).shouldTrackShow) {
          (window as any).trackShow((window as any).sourceType, (window as any).sourceParams);
          (window as any).shouldTrackShow = false;
        }
      } catch (e) {
        setLoadingGames(false);
        showBoundary(e);
        console.error('Failed to fetch games!', e);
      }
    };

    loadGames();
  }, [showBoundary]);

  return (
    <GlobalContext.Provider value={{
      loading: loadingGames || !areFontsLoaded,
      games,
      abTestId,
      gameOfDay: games.find((game: TGame) => game.isGameOfDay) ?? null,
      lookALikeGames: lookALikeGameIds
        .map((gameId) => games.find(({ id }) => id === gameId))
        .filter(Boolean) as TGame[],
    }}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  return useContext(GlobalContext);
};
